import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  concat,
  from,
} from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import type { Operation, DefaultOptions } from "@apollo/client";
import { get } from "svelte/store";
import { locale } from "svelte-i18n";
import { browser } from "$app/environment";
import cfg from "$lib/config/config";
import { baseHeaders, cmsHttpLink } from "./utils/cmsUtils";

type Headers = Record<string, string | undefined>;

const getLocale = () => {
  return get(locale)?.split("-")[0];
};

const shopifyEndpoint = cfg.SHOPIFY_ENDPOINT;

const NOSTO_SEARCH_ENDPOINT = `https://${cfg.NOSTO_SEARCH_ENDPOINT}`;

const NOSTO_ENDPOINT = `https://${cfg.NOSTO_ENDPOINT}`;

const ADMIN_GRAPHQL_ENDPOINT = cfg.SHOPIFY_ADMIN_ENDPOINT;

const shopifyHttpLink = createHttpLink({
  uri: shopifyEndpoint,
  fetch: browser ? window.fetch.bind(window) : fetch,
});

const adminHttpLink = createHttpLink({
  uri: ADMIN_GRAPHQL_ENDPOINT,
  fetch: browser ? window.fetch.bind(window) : fetch,
});

const cmsMiddleware = new ApolloLink((operation, forward) => {
  const existingHeaders = operation.getContext().headers || {};
  let headers: Headers = baseHeaders(existingHeaders);
  headers = {
    ...headers,
    "Accept-Language": getLocale(),
  };

  if (operation.getContext().clientIP) {
    headers["Shopify-Storefront-Buyer-IP"] = operation.getContext().clientIP;
  }

  if (operation.getContext().preview) {
    headers["Authorization"] = operation.getContext().preview?.token;
    operation.variables = {
      ...operation.variables,
      ...operation.getContext().preview?.variables,
    };
  }

  operation.setContext({ headers });
  return forward(operation);
});

const shopifyAuthMiddleware = new ApolloLink((operation, forward) => {
  const headers: Headers = {
    "Content-Type": "application/json",
    "Accept-Language": getLocale(),
    Accept: "application/json",
  };
  if (!browser) {
    const buyerIP = operation.getContext().clientIP;
    if (buyerIP) {
      headers["Shopify-Storefront-Buyer-IP"] = buyerIP;
    } else {
      console.warn(
        `buyer IP not given for Operation: '${operation.operationName}'`,
      );
    }
    // $env/dynamic/private can't be imported here as this file is run across
    // both client and server so falling back to using process here
    // https://kit.svelte.dev/docs/server-only-modules#how-it-works
    const accessToken = process.env.SHOPIFY_PRIVATE_ACCESS_TOKEN;
    if (!accessToken) {
      console.warn(
        "Storefront private access token is not set which will prevent SSR requests working. Please set it in the environment variable 'SHOPIFY_PRIVATE_ACCESS_TOKEN'",
      );
      headers["X-Shopify-Storefront-Access-Token"] = cfg.SHOPIFY_ACCESS_TOKEN;
    }
    headers["Shopify-Storefront-Private-Token"] = accessToken;
  } else {
    headers["X-Shopify-Storefront-Access-Token"] = cfg.SHOPIFY_ACCESS_TOKEN;
  }

  operation.setContext({ headers });
  return forward(operation);
});

const adminAuthMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      "X-Shopify-Access-Token": `${process.env.SHOPIFY_ADMIN_ACCESS_TOKEN}`,
    },
  });

  return forward(operation);
});

const nostoSearchAuthMiddleware = new ApolloLink((operation, forward) => {
  return forward(operation);
});

const link = ApolloLink.split(
  operation => operation.getContext().clientName === "cms",
  concat(cmsMiddleware, cmsHttpLink),
  ApolloLink.split(
    operation => operation.getContext().clientName === "admin",
    concat(adminAuthMiddleware, adminHttpLink),
    concat(shopifyAuthMiddleware, shopifyHttpLink),
  ),
);

const nostoSearchHttpLink = createHttpLink({
  uri: NOSTO_SEARCH_ENDPOINT,
  fetch,
});

const nostoSearchLink = ApolloLink.split(
  operation => operation.getContext().clientName === "nosto-search",
  concat(nostoSearchAuthMiddleware, nostoSearchHttpLink),
  link,
);

const nostoAuthMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Basic " + btoa(":" + cfg.NOSTO_API_KEY),
    },
  });

  return forward(operation);
});

const nostoHttpLink = createHttpLink({
  uri: NOSTO_ENDPOINT,
  fetch,
});

const nostoLink = ApolloLink.split(
  operation => operation.getContext().clientName === "nosto-recs",
  concat(nostoAuthMiddleware, nostoHttpLink),
  nostoSearchLink,
);

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  let opMsg = "";
  if (operation) {
    if (operation.getContext()?.clientName?.startsWith("nosto")) return;
    opMsg = `operation.name: '${operation?.operationName}'`;
    let variablesMsg = "";
    for (const key in operation.variables) {
      variablesMsg = `${variablesMsg} ${key}: ${operation.variables[key]},`;
    }
    opMsg = `${opMsg}, operation.vars:'${variablesMsg}'`;
  }

  if (graphQLErrors) {
    // Handles instances where Shopify doesn't return errors in an array.
    if (Array.isArray(graphQLErrors)) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}. ${opMsg} `,
        ),
      );
    } else {
      console.error(
        `[GraphQL error]: ${JSON.stringify(graphQLErrors)}. ${opMsg}`,
      );
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}. ${opMsg}`);
  }
});

const ssrOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

export default new ApolloClient({
  ssrMode: !browser,
  link: from([errorLink, nostoLink]),
  cache: new InMemoryCache({
    typePolicies: {
      SearchTermsFacet: {
        fields: {
          data: {
            merge(existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: browser ? undefined : ssrOptions,
});
